// TODO: Add types, check this file import type { Rate } from "../types/Rate";
import { fetchRatePlans } from "~/services/masterdataService";
import { useTravelDataStore } from "~/stores/travelDataStore";
import {
  RATE_PLAN_CATEGORIES,
  RATES_INPUT_DATE_FORMAT,
  RATES_DISPLAY_DATE_FORMAT,
} from "~/shared/constants";

import { formatDate } from "@citizenm/foundation/utils/date";

const useRate = () => {
  const { $goodbits } = useNuxtApp();
  const campaignRateCodes = ref([]);
  const travelData = useTravelDataStore();
  const { hotelsByCity } = useHotels();
  const currencyStore = useCurrencyStore();

  const alternativeOffersFetchState = useState(
    "alternativeOffersFetchState",
    () => "idle",
  );
  const alternativeOfferHotels = useState("alternativeOfferHotels", () => []);

  const totalGuests = computed(() => {
    const { guestsPerRoom, numberOfRooms } = travelData;
    return parseInt(guestsPerRoom) * parseInt(numberOfRooms);
  });

  const arrivalFormatted = computed(() => {
    const { arrivalDate } = travelData;
    return arrivalDate ? formatDate(arrivalDate, RATES_INPUT_DATE_FORMAT) : "";
  });

  const departureFormatted = computed(() => {
    const { departureDate } = travelData;
    return departureDate
      ? formatDate(departureDate, RATES_INPUT_DATE_FORMAT)
      : "";
  });

  const rangeDisplayFormatted = computed(() => {
    const { arrivalDate, departureDate } = travelData;
    if (!arrivalDate || !departureDate) return "";

    return `${formatDate(arrivalDate, RATES_DISPLAY_DATE_FORMAT)} - ${formatDate(
      departureDate,
      RATES_DISPLAY_DATE_FORMAT,
    )}`;
  });

  const redirectAppData = computed(() => {
    const { numberOfRooms, guestsPerRoom } = travelData;
    return {
      arrival: unref(arrivalFormatted),
      departure: unref(departureFormatted),
      rooms: numberOfRooms,
      adults: parseInt(guestsPerRoom),
    };
  });

  const parentCityCode = computed(() => {
    for (const [cityCode, hotels] of Object.entries(hotelsByCity.value)) {
      if (
        hotels.some((hotel) => hotel.propertyCode === travelData.hotelCodes)
      ) {
        return cityCode;
      }
    }
    return "";
  });

  function populateCampaignRateCodes(hotelRoomOffers) {
    if (!Array.isArray(hotelRoomOffers)) {
      campaignRateCodes.value = [];
      return;
    }

    campaignRateCodes.value = [
      ...new Set(
        hotelRoomOffers
          .filter((offer) =>
            offer.ratePlan?.ratePlanCategories?.includes(
              RATE_PLAN_CATEGORIES.CAMPAIGN,
            ),
          )
          .map((offer) => offer.ratePlan.ratePlanCode),
      ),
    ];
  }

  async function fetchAlternativeOffers() {
    try {
      alternativeOffersFetchState.value = "processing";
      alternativeOfferHotels.value = [];

      const hotelsInSameCity = hotelsByCity.value[parentCityCode.value] || [];

      if (!hotelsInSameCity.length) {
        alternativeOffersFetchState.value = "error";
        return { hotels: [], error: "No hotels found in the same city" };
      }

      const otherHotelsInSameCity = hotelsInSameCity.filter(
        (hotel) => hotel.propertyCode !== travelData.hotelCodes,
      );

      if (!otherHotelsInSameCity.length) {
        alternativeOffersFetchState.value = "error";
        return { hotels: [], error: "No alternative hotels found" };
      }

      const otherPropertyHotelCodes = otherHotelsInSameCity.map(
        (hotel) => hotel.propertyCode,
      );

      const otherOffers = await fetchOffers({
        hotelCodes: otherPropertyHotelCodes.join(","),
        arrivalDate: travelData.arrivalDate,
        departureDate: travelData.departureDate,
        numberOfRooms: travelData.numberOfRooms,
        promoCode: travelData.promoCode,
      });

      const combinedHotels = otherHotelsInSameCity
        .map((hotel) => {
          const hotelCode = hotel.propertyCode;
          const available =
            otherOffers.availability?.availableHotels?.includes(hotelCode) ??
            false;
          const bookable =
            otherOffers.availability?.bookableHotels?.includes(hotelCode) ??
            false;
          const bestBookablePrice = otherOffers.bestBookablePrices?.find(
            (price) => price.hotelCode === hotelCode,
          );

          return {
            name: hotel.name || "",
            image: hotel.heroImage,
            slug: hotel.slug || "",
            hotelCode,
            available,
            bookable,
            bestBookablePrice,
            description: hotel.description || "",
            shortDescription: hotel.shortDescription || "",
          };
        })
        .filter((hotel) => hotel.available || hotel.bookable);

      alternativeOfferHotels.value = combinedHotels;
      alternativeOffersFetchState.value =
        combinedHotels.length > 0 ? "success" : "error";

      if (combinedHotels.length > 0) {
        currencyStore.hotelOfferCurrency =
          combinedHotels[0].bestBookablePrice?.totalAmount?.currency;
      }

      return {
        hotels: combinedHotels,
        error: combinedHotels.length === 0 ? "No available hotels found" : null,
      };
    } catch (error) {
      alternativeOffersFetchState.value = "error";
      alternativeOfferHotels.value = [];
      return {
        hotels: [],
        error: error.message || "Failed to fetch alternative offers",
      };
    }
  }

  async function fetchOffers(travelData) {
    const {
      hotelCodes,
      numberOfRooms,
      promoCode,
      arrivalDate: arrivalDateDate,
      departureDate: departureDateDate,
    } = travelData;

    const arrivalDate = formatDate(arrivalDateDate, RATES_INPUT_DATE_FORMAT);
    const departureDate = formatDate(
      departureDateDate,
      RATES_INPUT_DATE_FORMAT,
    );

    try {
      const { data } = await $goodbits("/offers/v1/hotel-rooms", {
        query: {
          hotelCodes,
          arrivalDate,
          departureDate,
          numberOfRooms,
          totalGuests: totalGuests.value,
          promoCode,
          corporateCode: promoCode,
        },
      });

      if (data && data.hotelRoomOffers)
        populateCampaignRateCodes(data.hotelRoomOffers);

      return data;
    } catch (error) {
      console.error("Error fetching hotel room offers:", error);
      throw error;
    }
  }

  async function fetchRatePlansMasterdata(hotelRoomOffers, userLanguageCode) {
    let ratePlanCodes = "";

    hotelRoomOffers.forEach((hotelRoomOffer) => {
      if (ratePlanCodes.length === 0) {
        ratePlanCodes += hotelRoomOffer.ratePlan.ratePlanCode;
      } else {
        ratePlanCodes += `,${hotelRoomOffer.ratePlan.ratePlanCode}`;
      }
    });

    const ratePlansMasterData = await fetchRatePlans(
      localeToCfLocal(userLanguageCode),
      ratePlanCodes,
    )
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });

    return ratePlansMasterData;
  }

  function mapRateplans(hotelRoomOffers, ratePlanMasterData) {
    const rates = [];

    const basePricesGrossAmount = {};
    const basePricesGrossAmountExcl = {};
    const basePricesGrossAmountNightly = {};
    const basePricesGrossAmountExclNightly = {};

    const { searchEngineRate } = travelData;

    hotelRoomOffers.forEach((hotelRoomOffer) => {
      basePricesGrossAmount[hotelRoomOffer.ratePlan.ratePlanCode] =
        hotelRoomOffer.totalAmount.totalGrossAmount;
      basePricesGrossAmountExcl[hotelRoomOffer.ratePlan.ratePlanCode] =
        hotelRoomOffer.totalAmount.totalGrossAmountExcl;
      basePricesGrossAmountNightly[hotelRoomOffer.ratePlan.ratePlanCode] =
        hotelRoomOffer.totalAmount.totalGrossAmount /
        hotelRoomOffer.timeSlices.length;
      basePricesGrossAmountExclNightly[hotelRoomOffer.ratePlan.ratePlanCode] =
        hotelRoomOffer.totalAmount.totalGrossAmountExcl /
        hotelRoomOffer.timeSlices.length;
    });

    let masterDataRatePlanContainer = ratePlanMasterData;

    hotelRoomOffers.forEach((hotelRoomOffer) => {
      if (masterDataRatePlanContainer.length < 1) {
        return;
      }
      const hotelRoomMasterdata = masterDataRatePlanContainer.find(
        (ratePlanMaster) =>
          ratePlanMaster.code === hotelRoomOffer.ratePlan.ratePlanCode,
      );
      if (!hotelRoomMasterdata) {
        return;
      }
      masterDataRatePlanContainer = masterDataRatePlanContainer.filter(
        (ratePlanMasterData) =>
          ratePlanMasterData.code !== hotelRoomMasterdata.code,
      );
      const longDescription = hotelRoomMasterdata.longDescription.split("\n\n");

      const relevantBadges = [];
      if (
        hotelRoomOffer.baseRatePriceDifference &&
        hotelRoomOffer.baseRatePriceDifference.totalGrossAmount > 0
      ) {
        hotelRoomMasterdata.coloredTag.forEach((tag) => {
          relevantBadges.push({
            color: tag.backgroundColor,
            text: tag.textForTheTag,
          });
        });
      }

      if (
        searchEngineRate &&
        hotelRoomOffer.ratePlan.ratePlanCode === searchEngineRate
      ) {
        relevantBadges.push({
          color: "blue",
          text: "website.booking.your_rate_tag",
          withTranslationToken: true,
        });
      }

      let order = 99;
      if (
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("basicFlex") ||
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("directFlex") ||
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("plusFlex")
      ) {
        order = 1;
      }
      if (
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("basicNonRef") ||
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("directNonRef") ||
        hotelRoomOffer.ratePlan.ratePlanCategories?.includes("plusNonRef")
      ) {
        order = 2;
      }

      const newRate = {
        ...hotelRoomOffer,
        ...hotelRoomMasterdata,
        order,
        ratePlanCategory: hotelRoomOffer.ratePlan.ratePlanCategories
          ? hotelRoomOffer.ratePlan.ratePlanCategories[0].toLowerCase()
          : "basic",
        badges: relevantBadges,
        uniqueSellingPoints: hotelRoomMasterdata.uniqueSellingPoints,
        totalNetAmountNightly:
          hotelRoomOffer.totalAmount.totalNetAmount /
          hotelRoomOffer.timeSlices.length,
        strikeThroughPriceGrossAmount:
          basePricesGrossAmount[hotelRoomOffer.ratePlan?.baseRatePlanCode] ||
          false,
        strikeThroughPriceGrossAmountExcl:
          basePricesGrossAmountExcl[
            hotelRoomOffer.ratePlan?.baseRatePlanCode
          ] || false,
        strikeThroughPriceGrossAmountNightly:
          basePricesGrossAmountNightly[
            hotelRoomOffer.ratePlan?.baseRatePlanCode
          ] || false,
        strikeThroughPriceAmountExclNightly:
          basePricesGrossAmountExclNightly[
            hotelRoomOffer.ratePlan?.baseRatePlanCode
          ] || false,
        nights: hotelRoomOffer.timeSlices?.length,
        requiresLogin: false,
        requiresSubscription: false,
        highlights: hotelRoomMasterdata.uniqueSellingPoints.map((usp) => {
          return { name: usp.name, type: usp.type };
        }),
        priceInfoIncludingTax: "website.rates.rate_tax_included",
        priceInfoExludingTax: "website.rates.rate_tax_excluded",
        longDescription,
      };

      if (!hotelRoomOffer.validation.isAllowedToBool) {
        if (hotelRoomOffer.validation.errorCode === "userNotAuthenticated") {
          newRate.requiresLogin = true;
        }
        if (hotelRoomOffer.validation.errorCode === "noValidSubscription") {
          newRate.requiresSubscription = true;
        }
      }

      const relevantIncludedServices = [];
      hotelRoomOffer.timeSlices.forEach((timeslice) => {
        if (
          timeslice.includedServices &&
          timeslice.includedServices.length > 0
        ) {
          timeslice.includedServices.forEach((includedService) => {
            if (relevantIncludedServices.length === 0) {
              relevantIncludedServices.push(includedService);
            } else {
              let isServiceAlreadyIncluded = false;
              relevantIncludedServices.forEach((service) => {
                if (service.serviceCode === includedService.serviceCode) {
                  isServiceAlreadyIncluded = true;
                }
              });
              if (!isServiceAlreadyIncluded) {
                relevantIncludedServices.push(includedService);
              }
            }
          });
        }
      });
      if (relevantIncludedServices && relevantIncludedServices.length > 0) {
        newRate.includedServices = relevantIncludedServices;
      }
      rates.push(newRate);
    });
    return rates;
  }

  function getRelevantRatePlanCategories(
    availableRates,
    ratePlanCategoriesMasterData,
  ) {
    const rateCategories = [];

    availableRates.forEach((rate) => {
      const rateCategory = ratePlanCategoriesMasterData.find(
        (ratePlanCategoryMasterdata) =>
          ratePlanCategoryMasterdata.code.toLowerCase() ===
          rate.ratePlanCategory.toLowerCase(),
      );
      const includedRateCategory = rateCategories.find(
        (rateCategoryIncluded) =>
          rateCategoryIncluded.code.toLowerCase() ===
          rateCategory.code.toLowerCase(),
      );
      if (rateCategory && !includedRateCategory) {
        const uniqueSellingPoints = rateCategory.uniqueSellingPoints;
        const newHighlights = uniqueSellingPoints.map((usp) => usp.name);
        const newRateCategory = {
          code: rateCategory.code,
          name: rateCategory.name ?? "Basic",
          description: rateCategory.longDescription ?? "",
          highlights: newHighlights ?? [],
        };
        rateCategories.push(newRateCategory);
      }
    });
    return rateCategories;
  }

  /**
   * Splits the rates into campaign rates and available rates.
   * @param {Array} rates - The array of rates to split.
   * @returns {Object} An object containing the available rates and campaign rates.
   * TODO: Add types
   */
  function splitRates(rates) {
    const campaignRateCodesSet = new Set(campaignRateCodes.value);
    const campaignRates = [];
    const availableRates = [];

    let order = 1;

    rates.forEach((rate) => {
      if (campaignRateCodesSet.has(rate.ratePlan?.ratePlanCode)) {
        campaignRates.push({
          ...rate,
          ratePlanCategory: RATE_PLAN_CATEGORIES.CAMPAIGN,
          order: order++,
        });
      } else {
        availableRates.push(rate);
      }
    });

    return { availableRates, campaignRates };
  }

  return {
    totalGuests,
    arrivalFormatted,
    departureFormatted,
    redirectAppData,
    rangeDisplayFormatted,
    parentCityCode,
    alternativeOffersFetchState,
    alternativeOfferHotels,
    fetchOffers,
    fetchAlternativeOffers,
    fetchRatePlansMasterdata,
    mapRateplans,
    getRelevantRatePlanCategories,
    splitRates,
  };
};

export default useRate;
